import {Component, OnInit} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import {LengthErrorI} from '@common-module/app-interfaces/length-error-i';
import {AppMessageService} from '@common-module/app-services/app-message.service';
import {UiSpinnerService} from '@common-module/app-services/ui-spinner.service';
import {Driver} from '@common-module/modelinterfaces/driver.model';
import {MaxLength} from '@common-module/modelinterfaces/enums/max-length';
import {FullName} from "@common-module/modelinterfaces/full-name.model";
import {DriverService} from '@common-module/services/driver.service';
import {AppRegexp} from '@common-module/utils/app-regexp';
import {asyncIsExist, asyncIsExistInFormGroup} from '@common-module/validators/AppValidators';
import {DriverState} from '../../../../shared/constants/enums/driver-state.enum';
import {UrlImage} from '../../../../shared/constants/url-image';
import {DriverManagementService} from '../../../../shared/services/driver-management.service';
import {CommonModule} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {MaterialModule} from "@common-module/material.module";

enum Control {
  NESTED_GROUP = 'fullName',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  MIDDLE_NAME = 'middleName',
  PHONE = 'phone'
}

@Component({
  selector: 'app-driver-form',
  templateUrl: './driver-form.component.html',
  styleUrls: ['./driver-form.component.scss', '../../../../../../../common-module/src/lib/app-styles/dialog-common.scss'],
  imports: [FormsModule, ReactiveFormsModule, MaterialModule, CommonModule, TranslateModule]
})

export class DriverFormComponent implements OnInit {

  public readonly DEFAULT_AVATAR = UrlImage.DEFAULT_AVATAR;

  private readonly MIN_LENGTH_NAME = 1;

  public control = Control;
  public driver: Driver;
  public driverState = DriverState;
  public form: UntypedFormGroup;
  public state: DriverState;

  constructor(private appMessageService: AppMessageService,
              private driverService: DriverService,
              private driverManagementService: DriverManagementService,
              private uiSpinnerService: UiSpinnerService) {
  }

  get formControl(): { [p: string]: AbstractControl } {
    return this.form.controls;
  }

  get fullNameFormControl(): { [p: string]: AbstractControl } {
    return (this.form.controls[Control.NESTED_GROUP] as UntypedFormGroup).controls;
  }

  get formValue(): any {
    return this.form?.value;
  }

  get fullNameFormValue(): any {
    return (this.form.controls[Control.NESTED_GROUP] as UntypedFormGroup).value;
  }

  ngOnInit(): void {
    this.state = this.driverManagementService.instantState();
    this.driver = this.driverManagementService.tempDriver;
    if (!this.driver && this.state === DriverState.EDIT) {
      let driver = this.driverManagementService.instantDriver();
      this.driver = new Driver(
        driver?.id,
        driver?.name,
        driver?.phoneNumber,
        driver?.avatarUrl
      );
    }

    if (!this.driver) {
      this.changeAvatarRandom();
    }

    this.buildForm();
    if (!this.driver) {
      this.driver = this.createDriver();
    }
    if (this.driverManagementService.tempAvatar && this.driverManagementService.tempAvatar !== this.driver.avatarUrl) {
      this.form.markAsDirty();
    }
    if (this.driverManagementService.tempAvatar) {
      this.driver.avatarUrl = this.driverManagementService.tempAvatar;
      this.driverManagementService.tempAvatar = null;
    }
  }

  private buildForm(): void {
    this.form = new UntypedFormGroup({
        [Control.NESTED_GROUP]: new UntypedFormGroup({
            [Control.LAST_NAME]: new UntypedFormControl(this.driver?.name.last,
              [
                Validators.required,
                Validators.minLength(this.MIN_LENGTH_NAME),
                Validators.maxLength(MaxLength.DRIVER),
                Validators.pattern(AppRegexp.TEXT_WITHOUT_POINT)
              ]
            ),
            [Control.FIRST_NAME]: new UntypedFormControl(this.driver?.name.first,
              [
                Validators.minLength(this.MIN_LENGTH_NAME),
                Validators.maxLength(MaxLength.DRIVER),
                Validators.pattern(AppRegexp.TEXT_WITHOUT_POINT)
              ]
            ),
            [Control.MIDDLE_NAME]: new UntypedFormControl(this.driver?.name.middle,
              [
                Validators.minLength(this.MIN_LENGTH_NAME),
                Validators.maxLength(MaxLength.DRIVER),
                Validators.pattern(AppRegexp.TEXT_WITHOUT_POINT)
              ],
            )
          }, null, this.getAsyncFullNameValidator()
        ),
        [Control.PHONE]: new UntypedFormControl(this.driver?.phoneNumber, [Validators.pattern(AppRegexp.DRIVER_PHONE)],
          asyncIsExist(this.driverService.existByPhone.bind(this.driverService), this.driver?.phoneNumber)),
      }
    );
  }

  private getAsyncFullNameValidator(): AsyncValidatorFn {
    return asyncIsExistInFormGroup(
      this.driverService.existByFullName.bind(this.driverService),
      this.getFullNameList()
    );
  }

  private getFullNameList(): string[] {
    return [this.driver?.name.last, this.driver?.name.first, this.driver?.name.middle].filter(name => !!name);
  }

  private createDriver(): Driver {
    return new Driver(
      this.driver?.id || null,
      new FullName(
        this.fullNameFormValue[Control.LAST_NAME],
        this.fullNameFormValue[Control.FIRST_NAME],
        this.fullNameFormValue[Control.MIDDLE_NAME]
      ),
      this.formValue[Control.PHONE] ? this.formValue[Control.PHONE] : null,
      this.driver?.avatarUrl || UrlImage.DEFAULT_AVATAR
    );
  }

  public changeAvatarRandom(): void {
    this.uiSpinnerService.show();
    this.driverService.getAvatarRandom().subscribe(avatar => {
      this.uiSpinnerService.stop();
      this.driver.avatarUrl = avatar;
      this.form.markAsDirty();
    });
  }

  public changeAvatarManually(): void {
    this.driverManagementService.tempDriver = this.createDriver();
    this.driverManagementService.getAvatarList();
  }

  public onCancel(): void {
    this.driverManagementService.tempDriver = null;
    this.driverManagementService.changeState();
  }

  public onSubmit(): void {
    this.uiSpinnerService.show();
    const draft = this.createDriver();
    if (this.state === DriverState.EDIT) {
      this.driverService.update(Driver.toUpdate(draft.id, draft.name, draft.phoneNumber, draft.avatarUrl)).subscribe(updatedDriver => {
        this.appMessageService.openSnackBar('message.info.changes-saved');
        this.handlingChangeDriver(updatedDriver);
      });
      return;
    }
    this.driverService.save(Driver.toCreate(draft.name, draft.phoneNumber, draft.avatarUrl)).subscribe(updatedDriver => {
      this.appMessageService.openSnackBar('message.info.driver-added');
      this.handlingChangeDriver(updatedDriver);
    });
  }

  private handlingChangeDriver(driver: Driver): void {
    this.uiSpinnerService.stop();
    if (this.state === DriverState.EDIT) {
      this.driverManagementService.changeCurrentDriver(driver, true);
    }
    this.driverManagementService.changeState();
    this.driverManagementService.changeFireHistory(true);
  }

  public asLengthError(lengthError: LengthErrorI): LengthErrorI {
    return lengthError;
  }
}
